import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { formatCurrency, formatMinorCurrency } from '../../lib/formatPlan';

function formatDateOnly(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * v1.16.0 "Payout Interface" — schedule, amounts, and status at a glance for
 * an investment's payout structure. Every figure here is read directly off
 * the backend's PayoutSummary (`summary` from the payouts-list endpoint, or
 * equivalently `investment.payoutSummary`) — never recomputed by summing
 * raw payout rows. That's a deliberate constraint: the backend intentionally
 * allows flexible, out-of-order payout recording (admin discretion), so its
 * ledger is the only figure that can be trusted as authoritative.
 *
 * `onRecordPayout` is only passed on the admin side — omitting it hides the
 * action entirely for the investor's read-only view.
 */
export default function PayoutInterface({ summary, onRecordPayout }) {
  if (!summary) return null;

  return (
    <Card className="space-y-4">
      {summary.hasPendingPayout && (
        <div className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 flex items-center gap-2.5">
          <span className="h-2 w-2 rounded-full bg-warning shrink-0" />
          <p className="text-sm font-medium text-warning">Payout pending — this investment is behind its schedule.</p>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-sm font-semibold text-foreground">Payout interface</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={summary.principalPaid ? 'success' : 'neutral'}>
            Principal {summary.principalPaid ? 'returned' : 'outstanding'}
          </Badge>
          <Badge tone={summary.isPaidOut ? 'success' : 'neutral'}>{summary.isPaidOut ? 'Fully paid out' : 'Not fully paid out'}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-background-elevated p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">Schedule</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-foreground-subtle text-xs">Frequency</p>
              <p className="text-foreground font-medium mt-0.5">{summary.frequencyLabel || '—'}</p>
            </div>
            <div>
              <p className="text-foreground-subtle text-xs">Progress</p>
              <p className="text-foreground font-medium mt-0.5">
                Period {summary.currentPeriod} of {summary.totalPeriods}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-foreground-subtle text-xs">Next payout due</p>
              <p className="text-foreground font-medium mt-0.5">{formatDateOnly(summary.nextPayoutDueAt)}</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-background-elevated p-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">Amounts</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-foreground-subtle text-xs">Paid out so far</p>
              <p className="text-foreground font-medium mt-0.5">{formatCurrency(summary.totalPaidOut)}</p>
            </div>
            <div>
              <p className="text-foreground-subtle text-xs">Current period amount</p>
              <p className="text-foreground font-medium mt-0.5">{formatCurrency(summary.currentPeriodAmount)}</p>
            </div>
            <div>
              <p className="text-foreground-subtle text-xs">Total interest (life)</p>
              <p className="text-foreground font-medium mt-0.5">{formatCurrency(summary.totalInterest)}</p>
            </div>
            <div>
              <p className="text-foreground-subtle text-xs">Outstanding interest</p>
              <p className="text-foreground font-medium mt-0.5">{formatMinorCurrency(summary.outstandingInterestMinor)}</p>
            </div>
          </div>
          <div className="pt-2.5 border-t border-border">
            <p className="text-foreground-subtle text-xs">If settled today</p>
            <p className="text-foreground font-medium mt-0.5">
              {formatCurrency(summary.totalPaymentDue)}
              <span className="text-xs text-foreground-subtle font-normal">
                {' '}
                (Principal {formatCurrency(summary.totalPaymentDueBreakdown.principal)} · Interest{' '}
                {formatCurrency(summary.totalPaymentDueBreakdown.interest)})
              </span>
            </p>
          </div>
        </div>
      </div>

      {onRecordPayout && (
        <div className="pt-1 flex justify-end">
          <Button size="sm" variant="secondary" onClick={onRecordPayout}>
            Record payout
          </Button>
        </div>
      )}
    </Card>
  );
}
